import { MachinesHistory } from '../features/history/PageHistory.tsx';
import { usePageCrumbs } from '../lib/crumbs.ts';
import { FileDown, Hexagon, LayoutGrid, Plus, SearchX, Server } from '@orgus/ui/icons';
import { AnimatePresence, motion } from 'motion/react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { machineLiveness } from '@orgus/protocol';
import type { Job, Machine, Session } from '@orgus/protocol';
import { DataGate, PageBody } from '../components/shell/PageBody.tsx';
import { PageActions } from '../components/shell/TopBar.tsx';
import { Button, ErrorState, Tooltip, cn, toUiError } from '@orgus/ui';
import { ConfirmDialog } from '@orgus/ui';
import { EmptyState } from '@orgus/ui';
import { Skeleton } from '@orgus/ui';
import { BelowMinimum, FleetSummary } from '../features/fleet/FleetSummary.tsx';
import { FleetBadges, MachineFleet, badgesFor } from '../features/fleet/MachineFleet.tsx';
import type { FleetBadge } from '../features/fleet/model.ts';
import { fleetOf } from '../features/fleet/model.ts';
import { fleetActions, fleetReports, startMockSimulation, useFleet } from '../features/fleet/store.ts';
import { useTrust } from '../features/trust/useTrust.ts';
import { JobLogDialog } from '../features/jobs/JobLogDialog.tsx';
import { AddMachineDialog } from '../features/machines/AddMachineDialog.tsx';
import { CARD_SURFACE, MachineCard } from '../features/machines/MachineCard.tsx';
import { FleetGlance } from '../features/machines/FleetGlance.tsx';
import type { MachineCardProps } from '../features/machines/MachineCard.tsx';
import { MachineDrawer } from '../features/machines/MachineDrawer.tsx';
import { MachineLoad } from '../features/machine-load/MachineLoad.tsx';
import { VirtualMachineGrid } from '../features/machines/VirtualMachineGrid.tsx';
import { revokeMachine } from '../features/machines/revoke.ts';
import { ADD_MACHINE_PRESETS } from '../features/machines/pairing.ts';
import type { AddMachinePreset } from '../features/machines/pairing.ts';
import { AboutAgent } from '../features/machines/AboutAgent.tsx';
import { MachineAutonomy } from '../features/autonomy/MachineAutonomy.tsx';
import { MachineVm, PendingVms } from '../features/cloud/MachinesCloud.tsx';
import { MachinesCloudBand } from '../features/cloud/CloudBand.tsx';
import { useCloud } from '../features/cloud/data.ts';
import { vmOfMachine } from '../features/cloud/model.ts';
import { vmLine } from '../features/cloud/VmControls.tsx';
import { useRegisterCommands } from '../lib/commands.ts';
import type { Command } from '../lib/commands.ts';
import { plural } from '@orgus/ui';
import { useOrgus, useMachines } from '../lib/store.ts';
import { toast } from '@orgus/ui';
import { useNow } from '@orgus/ui';
import { Segmented } from '@orgus/ui';
import { machinePredicate } from '../features/ask/evaluate.ts';
import { HIVE_ABOVE, MachineHive } from '../features/machines/MachineHive.tsx';
import { ViewBar } from '../features/views/ViewBar.tsx';
import { useAskData, useWhere } from '../features/views/useWhere.ts';

function MachinesSkeleton() {
  return (
    <div className="mx-auto flex w-full max-w-(--layout-width-dashboard) flex-col gap-6" role="status" aria-label="Loading machines">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className={cn(CARD_SURFACE, 'flex flex-col gap-3 p-4')}>
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-3 w-32" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className={cn(CARD_SURFACE, 'flex flex-col gap-4 p-4')}>
            <div className="flex items-center gap-3">
              <Skeleton className="size-8" />
              <Skeleton className="h-4 w-32" />
            </div>
            <div className="grid grid-cols-2 gap-6">
              <Skeleton className="h-12" />
              <Skeleton className="h-12" />
            </div>
            <Skeleton className="h-3 w-3/4" />
          </div>
        ))}
      </div>
    </div>
  );
}

interface MachineLists {
  sessions: Session[];
  /** Sessions not ended. */
  live: Session[];
  jobs: Job[];
}
const NO_LISTS: MachineLists = { sessions: [], live: [], jobs: [] };
/** Above this many machines the grid is windowed and skips layout animation. */
const DENSE_ABOVE = 20;

/** The previous array when it holds the same items, so memoised children keep their props. */
function same<T>(prev: T[], next: T[]): T[] {
  return prev.length === next.length && prev.every((x, i) => x === next[i]) ? prev : next;
}

/**
 * The clock a card needs. An online machine's card reads the same at any moment while it stays
 * online, so it gets its own lastSeen and does not re-render on every tick of the page clock
 * (300 cards every 2 s was most of the page's work); stale and offline cards show an age, so
 * they get the real time.
 */
function cardNow(m: Machine, now: number): number {
  return machineLiveness(m, now) === 'online' ? m.lastSeen : now;
}

/** What a card shows of the fleet: its badges, and the version an "Update agent" item offers (or null). */
interface CardFleet {
  badges: FleetBadge[];
  updateTo: string | null;
}
const NO_FLEET: CardFleet = { badges: [], updateTo: null };

/** A card's menu action that failed: said in a banner over the grid, never in a toast. */
type Failure = { what: string; error: unknown };

/** A machine card that re-renders only when its own machine, lists, history, clock or fleet view change. */
const MachineCardItem = memo(function MachineCardItem({
  machine,
  onOpen,
  onRevoke,
  onFail,
  fleet,
  ...rest
}: Omit<MachineCardProps, 'onOpen' | 'onRevoke' | 'badges' | 'updateTo' | 'onUpdate' | 'onFail' | 'vm' | 'note'> & { onOpen: (id: string) => void; onRevoke: (m: Machine) => void; onFail: (f: Failure) => void; fleet: CardFleet }) {
  // A VM Orgus created (ADR 0106 decision 3): its status, its menu items above the common ones, its cost line.
  const vm = vmOfMachine(useCloud().state, machine.id);
  return (
    <MachineCard
      machine={machine}
      {...rest}
      onOpen={() => onOpen(machine.id)}
      onRevoke={() => onRevoke(machine)}
      badges={fleet.badges.length ? <FleetBadges badges={fleet.badges.slice(0, 1)} /> : null}
      updateTo={fleet.updateTo}
      onUpdate={() => fleetActions.updateMachine(machine.id).then(() => undefined)}
      onFail={onFail}
      vm={vm}
      note={vm ? vmLine(vm, rest.now) : null}
    />
  );
});

function newest(versions: string[]): string {
  return [...versions].sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))[0] ?? '';
}

/** Machines: every agent with its load, memory, sessions and jobs; add one by download and approval. */
export default function Machines() {
  const machines = useMachines();
  const now = useNow(2000);
  const [params, setParams] = useSearchParams();
  const openId = params.get('machine');
  const jobId = params.get('job');
  // ?add=1 opens Add machine (the link a phone sends to a computer); in demo mode ?add=<preset> forces a state.
  const addParam = params.get('add');
  const [adding, setAddingState] = useState(() => addParam !== null);
  const setAdding = useCallback(
    (v: boolean) => {
      setAddingState(v);
      if (!v && params.has('add')) {
        const next = new URLSearchParams(params);
        next.delete('add');
        setParams(next, { replace: true });
      }
    },
    [params, setParams],
  );
  const [revoking, setRevoking] = useState<Machine | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const { sessions, jobs, history, mode } = useOrgus(useShallow((s) => ({ sessions: s.sessions, jobs: s.jobs, history: s.machineHistory, mode: s.mode })));
  // Agent versions, rings and rollouts (fleet), and the minimum agent version (trust).
  const fleet = useFleet();
  const trust = useTrust();
  const minAgentVersion = trust.state?.minAgentVersion ?? null;
  const addPreset = mode === 'mock' && (ADD_MACHINE_PRESETS as readonly string[]).includes(addParam ?? '') ? (addParam as AddMachinePreset) : undefined;
  const consoleParam = params.get('console');
  const addReach = mode === 'mock' && (consoleParam === 'remote' || consoleParam === 'loopback') ? consoleParam : undefined;
  useEffect(() => {
    if (mode === 'mock' && fleet.state) startMockSimulation();
  }, [mode, fleet.state]);

  // Per machine: its sessions, live sessions and jobs. A machine whose lists did not change keeps
  // the same arrays, so its card (memoised) does not re-render when another machine reports.
  const prevByMachine = useRef<Record<string, MachineLists>>({});
  const byMachine = useMemo(() => {
    const out: Record<string, MachineLists> = {};
    for (const m of machines) out[m.id] = { sessions: [], live: [], jobs: [] };
    for (const s of Object.values(sessions)) out[s.machineId]?.sessions.push(s);
    for (const j of Object.values(jobs)) out[j.machineId]?.jobs.push(j);
    const prev = prevByMachine.current;
    for (const [id, v] of Object.entries(out)) {
      v.sessions.sort((a, b) => b.lastActivity - a.lastActivity);
      v.jobs.sort((a, b) => b.startedAt - a.startedAt);
      v.live = v.sessions.filter((s) => s.status !== 'ended');
      const p = prev[id];
      if (p) out[id] = { sessions: same(p.sessions, v.sessions), live: same(p.live, v.live), jobs: same(p.jobs, v.jobs) };
    }
    prevByMachine.current = out;
    return out;
  }, [machines, sessions, jobs]);
  const newestRunning = useMemo(() => newest(machines.map((m) => m.agentVersion)), [machines]);
  const latest = fleet.state?.latest ?? newestRunning;

  // Per machine: what its card shows of the fleet. The fleet state is polled, so a machine whose
  // view did not change keeps the same object and its memoised card does not re-render.
  const prevFleet = useRef<Record<string, CardFleet>>({});
  const cardFleet = useMemo(() => {
    const out: Record<string, CardFleet> = {};
    const fs = fleet.state;
    for (const m of machines) {
      const f = fleetOf(fs, m.id);
      const badges = badgesFor(m, f, { latest: fs?.latest ?? null, minAgentVersion });
      const updateTo = fs?.latest && m.online && f?.updatable !== false && m.agentVersion !== fs.latest ? fs.latest : null;
      const p = prevFleet.current[m.id];
      out[m.id] = !badges.length && !updateTo ? NO_FLEET : p && p.updateTo === updateTo && JSON.stringify(p.badges) === JSON.stringify(badges) ? p : { badges, updateTo };
    }
    prevFleet.current = out;
    return out;
  }, [machines, fleet.state, minAgentVersion]);
  const where = useWhere('machines');
  const askData = useAskData(now);
  const shownMachines = useMemo(() => (where.clauses.length ? machines.filter(machinePredicate(where.clauses, askData)) : machines), [machines, where.clauses, askData]);
  const layoutParam = params.get('layout');
  const layout: 'cards' | 'hive' = layoutParam === 'hive' || layoutParam === 'cards' ? layoutParam : machines.length > HIVE_ABOVE ? 'hive' : 'cards';
  const setLayout = (l: 'cards' | 'hive') =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        const auto = machines.length > HIVE_ABOVE ? 'hive' : 'cards';
        if (l === auto) n.delete('layout');
        else n.set('layout', l);
        return n;
      },
      { replace: true },
    );

  const setOpen = useCallback(
    (id: string | null) =>
      setParams(
        (p) => {
          const n = new URLSearchParams(p);
          if (id) n.set('machine', id);
          else n.delete('machine');
          return n;
        },
        { replace: true },
      ),
    [setParams],
  );
  const openMachine = machines.find((m) => m.id === openId);
  usePageCrumbs(openMachine ? [{ label: openMachine.name }] : []);
  const openJob = jobId ? jobs[jobId] : undefined;
  const setJob = useCallback(
    (id: string | null) =>
      setParams(
        (p) => {
          const n = new URLSearchParams(p);
          if (id) n.set('job', id);
          else n.delete('job');
          return n;
        },
        { replace: true },
      ),
    [setParams],
  );

  const exportInventory = useCallback(async () => {
    try {
      const n = await fleetReports.exportInventory();
      toast.success('Inventory exported', `${plural(n, 'machine')}, with versions and health.`);
    } catch (error) {
      setFailure({ what: 'The inventory was not exported', error });
    }
  }, []);
  const cmds = useMemo<Command[]>(
    () => [
      { id: 'machine.add', title: 'Add machine', subtitle: 'Download the Orgus agent for a new machine', group: 'Actions', icon: Plus, order: 20, perform: () => setAdding(true) },
      { id: 'machine.inventory', title: 'Export machine inventory', subtitle: 'Every machine with its version and health, as CSV', group: 'Actions', icon: FileDown, order: 21, perform: () => void exportInventory() },
    ],
    [exportInventory],
  );
  useRegisterCommands(cmds, [cmds]);

  const live = (m: Machine) => byMachine[m.id] ?? NO_LISTS;
  const revokeLive = revoking ? live(revoking).live.length : 0;
  const onRevoke = useCallback((m: Machine) => setRevoking(m), []);
  // Above 20 machines the grid is windowed: only the rows near the viewport are mounted, and
  // there is no layout animation (it measures every card on any change).
  const dense = machines.length > DENSE_ABOVE;
  const renderCard = (m: Machine) => (
    <MachineCardItem
      machine={m}
      history={history[m.id]}
      sessions={live(m).live}
      jobs={live(m).jobs}
      now={cardNow(m, now)}
      latestVersion={latest}
      onOpen={setOpen}
      onRevoke={onRevoke}
      onFail={setFailure}
      fleet={cardFleet[m.id] ?? NO_FLEET}
      selected={openId === m.id}
    />
  );
  const summary = <FleetSummary state={fleet.state} loading={fleet.loading} error={fleet.error} onRetry={fleet.refresh} machines={machines} />;
  const allSessions = useMemo(() => Object.values(sessions), [sessions]);
  const allJobs = useMemo(() => Object.values(jobs), [jobs]);

  return (
    <PageBody wide>
      <PageActions>
        {machines.length > 0 && (
          <Tooltip content="Every machine with its version and health, as CSV">
            <Button variant="ghost" size="sm" icon={<FileDown />} onClick={() => void exportInventory()}>
              Export inventory
            </Button>
          </Tooltip>
        )}
        <Tooltip content="Download the Orgus agent for a new machine">
          <Button variant="secondary" size="sm" icon={<Plus className="text-accent-text" />} onClick={() => setAdding(true)}>
            Add machine
          </Button>
        </Tooltip>
      </PageActions>
      <DataGate skeleton={<MachinesSkeleton />}>
        <div className="mx-auto flex w-full max-w-(--layout-width-dashboard) flex-col gap-6">
          {failure && (
            <div className={cn(CARD_SURFACE, 'px-4 py-3')}>
              <ErrorState variant="inline" error={toUiError(failure.error, { what: `${failure.what}.` })} action={<Button size="sm" variant="ghost" onClick={() => setFailure(null)}>Dismiss</Button>} />
            </div>
          )}
          {machines.length > 0 && <FleetGlance machines={machines} history={history} sessions={allSessions} jobs={allJobs} now={now} />}
          {machines.length > 0 && <MachinesHistory />}
          <BelowMinimum state={fleet.state} machines={machines} minAgentVersion={minAgentVersion} />
          <MachinesCloudBand />
          <PendingVms now={now} onFail={setFailure} />
          {machines.length > 0 ? (
            <section aria-label="Machines" className="flex flex-col gap-3">
              <ViewBar
                entity="machines"
                where={where}
                trailing={
                  <Segmented
                    label="Layout"
                    value={layout}
                    onChange={setLayout}
                    options={[
                      { value: 'cards', label: 'Cards', icon: <LayoutGrid />, iconOnly: true },
                      { value: 'hive', label: 'Hive: one cell per machine', icon: <Hexagon />, iconOnly: true },
                    ]}
                  />
                }
              />
              {shownMachines.length === 0 ? (
                <div className={CARD_SURFACE}>
                  <EmptyState icon={<SearchX />} title="No machines match" description={`No machine is ${where.text}.`} action={<Button onClick={where.clear}>Clear filters</Button>} />
                </div>
              ) : layout === 'hive' ? (
                <MachineHive
                  machines={shownMachines}
                  now={now}
                  activeId={openId}
                  onOpen={(id) => setOpen(id)}
                  liveSessions={(id) => (byMachine[id]?.sessions ?? []).filter((s) => s.status !== 'ended').length}
                />
              ) : dense ? (
                <VirtualMachineGrid items={shownMachines} renderItem={renderCard} />
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4">
                  <AnimatePresence initial={false}>
                    {shownMachines.map((m) => (
                      <motion.div
                        key={m.id}
                        layout
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.98 }}
                        transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
                      >
                        {renderCard(m)}
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </section>
          ) : (
            <div className={CARD_SURFACE}>
              <EmptyState
                size="page"
                icon={<Server />}
                title="No machines yet"
                description="Run the Orgus agent on a machine and approve it here; it reports its sessions, projects and load."
                action={
                  <Button variant="primary" size="lg" icon={<Plus />} onClick={() => setAdding(true)}>
                    Add machine
                  </Button>
                }
              />
            </div>
          )}
          {machines.length > 0 && summary}
        </div>
      </DataGate>

      <MachineDrawer
        machine={openMachine}
        history={openMachine ? history[openMachine.id] : undefined}
        sessions={openMachine ? live(openMachine).sessions : []}
        jobs={openMachine ? live(openMachine).jobs : []}
        now={now}
        onClose={() => setOpen(null)}
        onRevoke={() => openMachine && setRevoking(openMachine)}
        updateTo={openMachine ? (cardFleet[openMachine.id]?.updateTo ?? null) : null}
        onUpdate={() => (openMachine ? fleetActions.updateMachine(openMachine.id).then(() => undefined) : Promise.resolve())}
        onFail={setFailure}
        onOpenJob={setJob}
        load={openMachine && <MachineLoad machine={openMachine} now={now} />}
      >
        {openMachine && <MachineVm machineId={openMachine.id} now={now} />}
        {openMachine && <MachineAutonomy machine={openMachine} trust={trust.state?.nodes.find((n) => n.machineId === openMachine.id) ?? null} />}
        {openMachine && fleet.state && (
          <MachineFleet machine={openMachine} fleet={fleetOf(fleet.state, openMachine.id)} latest={fleet.state.latest} minAgentVersion={minAgentVersion} workspaceChannel={fleet.state.policy?.channel} />
        )}
        {openMachine && <AboutAgent machine={openMachine} fleet={fleet.state ? fleetOf(fleet.state, openMachine.id) : null} trust={trust.state?.nodes.find((n) => n.machineId === openMachine.id) ?? null} />}
      </MachineDrawer>

      <JobLogDialog job={openJob} machine={openJob ? machines.find((m) => m.id === openJob.machineId) : undefined} onClose={() => setJob(null)} />

      <AddMachineDialog open={adding} onOpenChange={setAdding} onOpenMachine={(id) => setOpen(id)} preset={addPreset} reach={addReach} />

      <ConfirmDialog
        open={!!revoking}
        onOpenChange={(v) => !v && setRevoking(null)}
        title={`Remove ${revoking?.name ?? 'this machine'} from Orgus?`}
        description={
          revoking?.local
            ? `This is the hub's own machine. Its ${revokeLive ? `${plural(revokeLive, 'live session')}, ` : ''}projects and pipelines leave Orgus, and it stays removed until you restart orgus up; then it comes back as a new machine. Nothing on the machine itself is touched.`
            : `It disconnects now and its key stops working. Its ${revokeLive ? `${plural(revokeLive, 'live session')}, ` : ''}projects and pipelines leave Orgus; nothing on the machine itself is touched. To bring it back, add it again with a new command.`
        }
        confirmLabel="Remove from Orgus"
        onConfirm={async () => {
          if (!revoking) return;
          const name = revoking.name;
          await revokeMachine(revoking.id);
          if (openId === revoking.id) setOpen(null);
          toast.success(`Removed ${name} from Orgus`, 'It can no longer connect.');
        }}
      />
    </PageBody>
  );
}
